import CardAnimations from './CardAnimations.js';
import { ANIMATION_DURATIONS } from '../config/animationConfig.js';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

class ScoreMarkerRenderer {
    constructor(app, team1Marker, team2Marker) {
        this.app = app;
        this.team1Marker = team1Marker;
        this.team2Marker = team2Marker;
        this.sprites = null;
        this.cardWidth = 100;
        this.cardHeight = 150;
        this.margin = 20;
        this.scaleRatio = 1; // Scale ratio for responsive card sizing
        // Track previous states to detect transitions
        this.team1PreviousState = this.team1Marker.isInMalas();
        this.team2PreviousState = this.team2Marker.isInMalas();
        this.team1PreviousFaceUp = this.team1Marker.isJokerFaceUp();
        this.team2PreviousFaceUp = this.team2Marker.isJokerFaceUp();
        this.team1PreviousScore = this.team1Marker.score;
        this.team2PreviousScore = this.team2Marker.score;
        // Animation flags
        this.team1Animating = false;
        this.team2Animating = false;
        this.team1PositionAnimating = false;
        this.team2PositionAnimating = false;

        // Callback for history click
        this.onHistoryClick = null;
    }

    setOnHistoryClick(callback) {
        this.onHistoryClick = callback;
    }

    /**
     * Force update of previous scores to match current scores
     * Used when restoring a saved game to sync the renderer state
     */
    forceUpdatePreviousScores() {
        debugLog('[ScoreMarkerRenderer] Forcing update of previous scores');
        debugLog('   - Team1: current score:', this.team1Marker.score, ', previous:', this.team1PreviousScore);
        debugLog('   - Team2: current score:', this.team2Marker.score, ', previous:', this.team2PreviousScore);

        this.team1PreviousScore = this.team1Marker.score;
        this.team2PreviousScore = this.team2Marker.score;
        this.team1PreviousState = this.team1Marker.isInMalas();
        this.team2PreviousState = this.team2Marker.isInMalas();
        this.team1PreviousFaceUp = this.team1Marker.isJokerFaceUp();
        this.team2PreviousFaceUp = this.team2Marker.isJokerFaceUp();

        debugLog('   - After update - Team1 previous:', this.team1PreviousScore, ', Team2 previous:', this.team2PreviousScore);
    }

    /**
     * Restore joker positions instantly without animation
     * Used when loading a saved game
     */
    async restoreJokerPositions() {
        // Wait for sprites to be initialized (max 5 seconds)
        const maxWaitTime = 5000;
        const checkInterval = 100;
        let waited = 0;

        while (!this.sprites && waited < maxWaitTime) {
            debugLog('[ScoreMarkerRenderer] Waiting for sprites to initialize...');
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            waited += checkInterval;
        }

        if (!this.sprites) {
            console.error('[ScoreMarkerRenderer] Cannot restore positions - sprites not initialized after', maxWaitTime, 'ms');
            return;
        }

        debugLog('[ScoreMarkerRenderer] Restoring joker positions instantly');
        const { joker1Sprite, joker2Sprite, team1CardSprite, team2CardSprite } = this.sprites;

        // Restore Team 1 joker position
        if (joker1Sprite && team1CardSprite) {
            const team1RelPos = this.getScaledJokerPosition(this.team1Marker.getVisibleOros());
            const targetPos = this.calculateJokerPosition(
                team1CardSprite.x,
                team1CardSprite.y,
                team1RelPos,
                this.team1Marker.isJokerFaceUp(),
                team1CardSprite.rotation,
                { x: team1CardSprite.anchor.x, y: team1CardSprite.anchor.y }
            );

            joker1Sprite.x = targetPos.x;
            joker1Sprite.y = targetPos.y;
            joker1Sprite.rotation = targetPos.rotation;

            debugLog(`   - Team1 joker restored to: x=${targetPos.x.toFixed(2)}, y=${targetPos.y.toFixed(2)}, rot=${targetPos.rotation.toFixed(2)}`);
        }

        // Restore Team 2 joker position
        if (joker2Sprite && team2CardSprite) {
            const team2RelPos = this.getScaledJokerPosition(this.team2Marker.getVisibleOros());
            const targetPos = this.calculateJokerPosition(
                team2CardSprite.x,
                team2CardSprite.y,
                team2RelPos,
                this.team2Marker.isJokerFaceUp(),
                team2CardSprite.rotation,
                { x: team2CardSprite.anchor.x, y: team2CardSprite.anchor.y }
            );

            joker2Sprite.x = targetPos.x;
            joker2Sprite.y = targetPos.y;
            joker2Sprite.rotation = targetPos.rotation;

            debugLog(`   - Team2 joker restored to: x=${targetPos.x.toFixed(2)}, y=${targetPos.y.toFixed(2)}, rot=${targetPos.rotation.toFixed(2)}`);
        }

        // Update the previous scores to prevent animation on next update
        this.forceUpdatePreviousScores();
    }

    /**
     * Get the relative position and rotation for the joker based on visible oros
     * Positions are relative to the base card (8 or 9 of oros)
     * @param {number} visibleOros - Number of visible oros (0-8)
     * @returns {Object} { offsetX, offsetY, angle } relative to base card
     */
    static getJokerRelativePosition(visibleOros) {
        // Position map for each visible oro count (0-8)
        const positions = {
            0: { offsetX: 0, offsetY: 0, angle: 0 },      // 0, 9, or 18 points
            1: { offsetX: 80, offsetY: 0, angle: 45 },    // 1 or 10 points
            2: { offsetX: 0, offsetY: 40, angle: 0 },     // 2 or 11 points
            3: { offsetX: 80, offsetY: 35, angle: 45 },   // 3 or 12 points
            4: { offsetX: 0, offsetY: 70, angle: 0 },     // 4 or 13 points
            5: { offsetX: 50, offsetY: 40, angle: 0 },    // 5 or 14 points
            6: { offsetX: 0, offsetY: 100, angle: 0 },    // 6 or 15 points
            7: { offsetX: 80, offsetY: 100, angle: 45 },  // 7 or 16 points
            8: { offsetX: 0, offsetY: 140, angle: 0 }     // 8 or 17 points
        };

        return positions[visibleOros] || positions[0];
    }

    /**
     * Get scaled joker relative position based on current scale ratio
     * @param {number} visibleOros - Number of visible oros (0-8)
     * @returns {Object} { offsetX, offsetY, angle } scaled to current card size
     */
    getScaledJokerPosition(visibleOros) {
        const basePos = ScoreMarkerRenderer.getJokerRelativePosition(visibleOros);
        return {
            offsetX: basePos.offsetX * this.scaleRatio,
            offsetY: basePos.offsetY * this.scaleRatio,
            angle: basePos.angle
        };
    }

    /**
     * Calculate absolute position for joker based on base card position and relative offset
     * @param {number} baseCardX - X position of base card
     * @param {number} baseCardY - Y position of base card
     * @param {Object} relativePos - Relative position from getJokerRelativePosition
     * @param {boolean} isFaceUp - Whether joker is face up
     * @param {number} baseCardRotation - Rotation of the base card in radians (default 0)
     * @param {Object} baseCardAnchor - Anchor point of base card (default {x:0, y:0})
     */
    calculateJokerPosition(baseCardX, baseCardY, relativePos, isFaceUp, baseCardRotation = 0, baseCardAnchor = { x: 0, y: 0 }) {
        const { offsetX, offsetY, angle } = relativePos;

        // Convert angle to radians
        const angleRad = (angle * Math.PI) / 180;

        // If base card is rotated, we need to rotate the offset vector
        let finalOffsetX = offsetX;
        let finalOffsetY = offsetY;

        if (baseCardRotation !== 0) {
            // Rotate the offset vector by the base card rotation
            const cos = Math.cos(baseCardRotation);
            const sin = Math.sin(baseCardRotation);
            finalOffsetX = offsetX * cos - offsetY * sin;
            finalOffsetY = offsetX * sin + offsetY * cos;
        }

        if (isFaceUp) {
            // When face up, the card is rotated 180°
            // The anchor is (0,0) to keep the same offset logic
            return {
                x: baseCardX + finalOffsetX,
                y: baseCardY + finalOffsetY,
                rotation: angleRad + baseCardRotation,
                anchor: { x: 0, y: 0 }
            };
        } else {
            // When face down, position relative to top-left corner of base card
            return {
                x: baseCardX + finalOffsetX,
                y: baseCardY + finalOffsetY,
                rotation: angleRad + baseCardRotation,
                anchor: { x: 0, y: 0 }
            };
        }
    }

    async render() {
        if (!this.app || !this.app.screen || !this.app.stage) {
            console.warn('[ScoreMarkerRenderer] Cannot render - app or screen is null');
            return;
        }

        const { Sprite, Assets, Graphics } = await import('pixi.js');

        // Enable z-index sorting on stage
        this.app.stage.sortableChildren = true;

        // Team 1 marker (8 of oros) - bottom left corner
        const team1Card = await Assets.load('assets/images/deck/108.png');
        const team1CardSprite = new Sprite(team1Card);
        team1CardSprite.label = 'Team1_8Oros';
        team1CardSprite.width = this.cardWidth;
        team1CardSprite.height = this.cardHeight;
        team1CardSprite.x = this.margin;
        team1CardSprite.y = this.app.screen.height - this.cardHeight - this.margin;
        team1CardSprite.zIndex = 200; // Higher than card containers (100) to stay on top
        this.app.stage.addChild(team1CardSprite);

        // Team 1 joker
        const cardBack = localStorage.getItem('cardBack') || '001';
        const joker1Image = this.team1Marker.isInMalas() ? cardBack : '000';
        const joker1 = await Assets.load(`assets/images/deck/${joker1Image}.png`);
        const joker1Sprite = new Sprite(joker1);
        joker1Sprite.label = 'Team1_Joker';
        joker1Sprite.width = this.cardWidth;
        joker1Sprite.height = this.cardHeight;
        joker1Sprite.zIndex = 200; // Higher than card containers (100) to stay on top

        // Set initial position based on score
        const team1RelPos = this.getScaledJokerPosition(this.team1Marker.getVisibleOros());
        const team1Pos = this.calculateJokerPosition(
            team1CardSprite.x,
            team1CardSprite.y,
            team1RelPos,
            this.team1Marker.isJokerFaceUp()
        );
        joker1Sprite.anchor.set(team1Pos.anchor.x, team1Pos.anchor.y);
        joker1Sprite.x = team1Pos.x;
        joker1Sprite.y = team1Pos.y;
        joker1Sprite.rotation = team1Pos.rotation;
        this.app.stage.addChild(joker1Sprite);

        // Create history button for Team 1
        const historyBtn1 = new Graphics();
        this.drawHistoryButton(historyBtn1);
        historyBtn1.label = 'Team1_HistoryBtn';
        historyBtn1.eventMode = 'static';
        historyBtn1.cursor = 'pointer';
        historyBtn1.alpha = 0.6;
        historyBtn1.zIndex = 250; // Even higher than markers (200) to ensure clickability
        historyBtn1.on('pointerdown', () => {
            if (this.onHistoryClick) this.onHistoryClick(1);
        });

        // Position relative to Team 1 card (top-left of the card area?)
        // Team1 card is at bottom left. Let's put it slightly above/over it.
        historyBtn1.x = team1CardSprite.x + this.cardWidth / 2;
        historyBtn1.y = team1CardSprite.y - 30; // Above the card
        this.app.stage.addChild(historyBtn1);

        // Team 2 marker (9 of oros) - top right corner, rotated 270 degrees
        const team2Card = await Assets.load('assets/images/deck/109.png');
        const team2CardSprite = new Sprite(team2Card);
        team2CardSprite.label = 'Team2_9Oros';
        team2CardSprite.width = this.cardWidth;
        team2CardSprite.height = this.cardHeight;
        // Position at top right with rotation 270 degrees (3π/2 radians)
        team2CardSprite.anchor.set(0, 0); // Anchor at top-left
        team2CardSprite.rotation = -Math.PI / 2; // 270 degrees = -90 degrees
        // When rotated -90°, the card extends downward (height) and leftward (width)
        // So we need to offset by cardHeight from the right edge and margin from top
        team2CardSprite.x = this.app.screen.width - this.margin - this.cardHeight;
        team2CardSprite.y = this.margin + this.cardWidth;
        team2CardSprite.zIndex = 200; // Higher than card containers (100) to stay on top
        this.app.stage.addChild(team2CardSprite);

        // Create history button for Team 2
        const historyBtn2 = new Graphics();
        this.drawHistoryButton(historyBtn2);
        historyBtn2.label = 'Team2_HistoryBtn';
        historyBtn2.eventMode = 'static';
        historyBtn2.cursor = 'pointer';
        historyBtn2.alpha = 0.6;
        historyBtn2.zIndex = 250; // Even higher than markers (200) to ensure clickability
        historyBtn2.on('pointerdown', () => {
            if (this.onHistoryClick) this.onHistoryClick(2);
        });

        // Position relative to Team 2 card (near top right)
        // Team 2 card is rotated. 
        // x is roughly screenWidth - margin - cardHeight.
        historyBtn2.x = team2CardSprite.x + this.cardHeight / 2; // Centered horizontally relative to the "top" of the rotated card
        historyBtn2.y = team2CardSprite.y + 30; // Below the "top" which is visually at the top
        this.app.stage.addChild(historyBtn2);

        // Team 2 joker
        const joker2Image = this.team2Marker.isInMalas() ? cardBack : '000';
        const joker2 = await Assets.load(`assets/images/deck/${joker2Image}.png`);
        const joker2Sprite = new Sprite(joker2);
        joker2Sprite.label = 'Team2_Joker';
        joker2Sprite.width = this.cardWidth;
        joker2Sprite.height = this.cardHeight;
        joker2Sprite.zIndex = 200; // Higher than card containers (100) to stay on top

        // Set initial position based on score
        const team2RelPos = this.getScaledJokerPosition(this.team2Marker.getVisibleOros());
        const team2Pos = this.calculateJokerPosition(
            team2CardSprite.x,
            team2CardSprite.y,
            team2RelPos,
            this.team2Marker.isJokerFaceUp(),
            team2CardSprite.rotation,
            { x: team2CardSprite.anchor.x, y: team2CardSprite.anchor.y }
        );
        joker2Sprite.anchor.set(team2Pos.anchor.x, team2Pos.anchor.y);
        joker2Sprite.x = team2Pos.x;
        joker2Sprite.y = team2Pos.y;
        joker2Sprite.rotation = team2Pos.rotation;
        this.app.stage.addChild(joker2Sprite);

        // Store sprites for updates
        this.sprites = {
            team1CardSprite,
            joker1Sprite,
            team2CardSprite,
            joker2Sprite,
            historyBtn1,
            historyBtn2
        };

        return this.sprites;
    }

    drawHistoryButton(graphics) {
        graphics.clear();
        graphics.circle(0, 0, 20);
        graphics.fill({ color: 0x000000, alpha: 0.5 });
        graphics.stroke({ width: 2, color: 0xffffff, alpha: 0.8 });

        // Draw a simple "list" icon or "i"
        // 3 horizontal lines
        graphics.moveTo(-10, -5);
        graphics.lineTo(10, -5);
        graphics.stroke({ width: 2, color: 0xffffff });

        graphics.moveTo(-10, 0);
        graphics.lineTo(10, 0);
        graphics.stroke({ width: 2, color: 0xffffff });

        graphics.moveTo(-10, 5);
        graphics.lineTo(10, 5);
        graphics.stroke({ width: 2, color: 0xffffff });
    }

    async update() {
        if (!this.sprites) return;

        const { Assets } = await import('pixi.js');
        const { joker1Sprite, joker2Sprite, team1CardSprite, team2CardSprite } = this.sprites;
        const cardBack = localStorage.getItem('cardBack') || '001';

        // Update Team 1 joker
        if (joker1Sprite) {
            const currentIsInMalas = this.team1Marker.isInMalas();
            const currentFaceUp = this.team1Marker.isJokerFaceUp();
            const stateChanged = currentIsInMalas !== this.team1PreviousState;
            const faceUpChanged = currentFaceUp !== this.team1PreviousFaceUp;
            const scoreChanged = this.team1Marker.score !== this.team1PreviousScore;

            // Handle flip animation when face up state changes (malas/buenas or reaching 18)
            if (faceUpChanged && !this.team1Animating) {
                this.team1Animating = true;
                this.team1PreviousState = currentIsInMalas;
                this.team1PreviousFaceUp = currentFaceUp;

                // Validate sprite before animation
                if (joker1Sprite && !joker1Sprite.destroyed) {
                    // Load the new texture
                    const joker1Image = currentIsInMalas ? cardBack : '000';
                    const newTexture = await Assets.load(`assets/images/deck/${joker1Image}.png`);
                    const finalRotation = currentFaceUp ? Math.PI : 0;

                    // Animate the flip using CardAnimations utility
                    await CardAnimations.flipCard(joker1Sprite, newTexture, finalRotation);
                }

                this.team1Animating = false;
            }

            // Handle position animation when score changes
            if (scoreChanged && !this.team1PositionAnimating && !this.team1Animating) {
                this.team1PositionAnimating = true;
                this.team1PreviousScore = this.team1Marker.score;

                const team1RelPos = this.getScaledJokerPosition(this.team1Marker.getVisibleOros());
                const targetPos = this.calculateJokerPosition(
                    team1CardSprite.x,
                    team1CardSprite.y,
                    team1RelPos,
                    this.team1Marker.isJokerFaceUp()
                );

                // Animate position, rotation, and anchor changes
                await this.animateJokerPosition(joker1Sprite, targetPos);

                this.team1PositionAnimating = false;
            }

            // Update position if not animating
            if (!this.team1Animating && !this.team1PositionAnimating) {
                const team1RelPos = this.getScaledJokerPosition(this.team1Marker.getVisibleOros());
                const team1Pos = this.calculateJokerPosition(
                    team1CardSprite.x,
                    team1CardSprite.y,
                    team1RelPos,
                    this.team1Marker.isJokerFaceUp()
                );

                joker1Sprite.anchor.set(team1Pos.anchor.x, team1Pos.anchor.y);
                joker1Sprite.x = team1Pos.x;
                joker1Sprite.y = team1Pos.y;
                joker1Sprite.rotation = team1Pos.rotation;
            }
        }

        // Update Team 2 joker
        if (joker2Sprite) {
            const currentIsInMalas = this.team2Marker.isInMalas();
            const currentFaceUp = this.team2Marker.isJokerFaceUp();
            const stateChanged = currentIsInMalas !== this.team2PreviousState;
            const faceUpChanged = currentFaceUp !== this.team2PreviousFaceUp;
            const scoreChanged = this.team2Marker.score !== this.team2PreviousScore;

            // Handle flip animation when face up state changes (malas/buenas or reaching 18)
            if (faceUpChanged && !this.team2Animating) {
                this.team2Animating = true;
                this.team2PreviousState = currentIsInMalas;
                this.team2PreviousFaceUp = currentFaceUp;

                // Validate sprite before animation
                if (joker2Sprite && !joker2Sprite.destroyed) {
                    // Load the new texture
                    const joker2Image = currentIsInMalas ? cardBack : '000';
                    const newTexture = await Assets.load(`assets/images/deck/${joker2Image}.png`);
                    const finalRotation = currentFaceUp ? Math.PI : 0;

                    // Animate the flip using CardAnimations utility
                    await CardAnimations.flipCard(joker2Sprite, newTexture, finalRotation);
                }

                this.team2Animating = false;
            }

            // Handle position animation when score changes
            if (scoreChanged && !this.team2PositionAnimating && !this.team2Animating) {
                this.team2PositionAnimating = true;
                this.team2PreviousScore = this.team2Marker.score;

                const team2RelPos = this.getScaledJokerPosition(this.team2Marker.getVisibleOros());
                const targetPos = this.calculateJokerPosition(
                    team2CardSprite.x,
                    team2CardSprite.y,
                    team2RelPos,
                    this.team2Marker.isJokerFaceUp(),
                    team2CardSprite.rotation,
                    { x: team2CardSprite.anchor.x, y: team2CardSprite.anchor.y }
                );

                // Animate position, rotation, and anchor changes
                await this.animateJokerPosition(joker2Sprite, targetPos);

                this.team2PositionAnimating = false;
            }

            // Update position if not animating
            if (!this.team2Animating && !this.team2PositionAnimating) {
                const team2RelPos = this.getScaledJokerPosition(this.team2Marker.getVisibleOros());
                const team2Pos = this.calculateJokerPosition(
                    team2CardSprite.x,
                    team2CardSprite.y,
                    team2RelPos,
                    this.team2Marker.isJokerFaceUp(),
                    team2CardSprite.rotation,
                    { x: team2CardSprite.anchor.x, y: team2CardSprite.anchor.y }
                );

                joker2Sprite.anchor.set(team2Pos.anchor.x, team2Pos.anchor.y);
                joker2Sprite.x = team2Pos.x;
                joker2Sprite.y = team2Pos.y;
                joker2Sprite.rotation = team2Pos.rotation;
            }
        }
    }

    /**
     * Animates the joker position smoothly to a new position
     */
    async animateJokerPosition(sprite, targetPos) {
        return new Promise((resolve) => {
            const duration = ANIMATION_DURATIONS.JOKER_ANIMATION;
            const start = performance.now();

            const startPos = {
                x: sprite.x,
                y: sprite.y,
                rotation: sprite.rotation,
                anchorX: sprite.anchor.x,
                anchorY: sprite.anchor.y
            };

            // Easing function (ease in-out)
            const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

            const animate = (time) => {
                const t = Math.min(1, (time - start) / duration);
                const easedT = ease(t);

                // Interpolate position
                sprite.x = startPos.x + (targetPos.x - startPos.x) * easedT;
                sprite.y = startPos.y + (targetPos.y - startPos.y) * easedT;

                // Interpolate rotation (handle wrapping around 2π)
                let rotationDiff = targetPos.rotation - startPos.rotation;
                if (rotationDiff > Math.PI) rotationDiff -= 2 * Math.PI;
                if (rotationDiff < -Math.PI) rotationDiff += 2 * Math.PI;
                sprite.rotation = startPos.rotation + rotationDiff * easedT;

                // Interpolate anchor (usually changes instantly but smooth looks better)
                sprite.anchor.x = startPos.anchorX + (targetPos.anchor.x - startPos.anchorX) * easedT;
                sprite.anchor.y = startPos.anchorY + (targetPos.anchor.y - startPos.anchorY) * easedT;

                if (t < 1) {
                    requestAnimationFrame(animate);
                } else {
                    // Ensure final state
                    sprite.x = targetPos.x;
                    sprite.y = targetPos.y;
                    sprite.rotation = targetPos.rotation;
                    sprite.anchor.set(targetPos.anchor.x, targetPos.anchor.y);
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    }

    /**
     * Updates card dimensions for responsive sizing
     * @param {number} width - New card width
     * @param {number} height - New card height
     */
    setCardSize(width, height) {
        if (!this.app || !this.app.screen) {
            console.warn('[ScoreMarkerRenderer] Cannot set card size - app or screen is null');
            return;
        }

        this.scaleRatio = width / 100; // 100 is the base card width
        this.cardWidth = width;
        this.cardHeight = height;

        // Update all sprites if they exist
        if (this.sprites) {
            const { team1CardSprite, joker1Sprite, team2CardSprite, joker2Sprite } = this.sprites;

            if (team1CardSprite) {
                team1CardSprite.width = width;
                team1CardSprite.height = height;
                team1CardSprite.x = this.margin;
                team1CardSprite.y = this.app.screen.height - height - this.margin;
            }

            if (team2CardSprite) {
                team2CardSprite.width = width;
                team2CardSprite.height = height;
                team2CardSprite.x = this.app.screen.width - this.margin - height;
                team2CardSprite.y = this.margin + width;
            }

            // Re-position jokers based on current score with scaled positions
            if (joker1Sprite && team1CardSprite) {
                const team1RelPos = this.getScaledJokerPosition(this.team1Marker.getVisibleOros());
                const team1Pos = this.calculateJokerPosition(
                    team1CardSprite.x,
                    team1CardSprite.y,
                    team1RelPos,
                    this.team1Marker.isJokerFaceUp()
                );
                joker1Sprite.width = width;
                joker1Sprite.height = height;
                joker1Sprite.anchor.set(team1Pos.anchor.x, team1Pos.anchor.y);
                joker1Sprite.x = team1Pos.x;
                joker1Sprite.y = team1Pos.y;
                joker1Sprite.rotation = team1Pos.rotation;
            }

            if (joker2Sprite && team2CardSprite) {
                const team2RelPos = this.getScaledJokerPosition(this.team2Marker.getVisibleOros());
                const team2Pos = this.calculateJokerPosition(
                    team2CardSprite.x,
                    team2CardSprite.y,
                    team2RelPos,
                    this.team2Marker.isJokerFaceUp(),
                    team2CardSprite.rotation,
                    { x: team2CardSprite.anchor.x, y: team2CardSprite.anchor.y }
                );
                joker2Sprite.width = width;
                joker2Sprite.height = height;
                joker2Sprite.anchor.set(team2Pos.anchor.x, team2Pos.anchor.y);
                joker2Sprite.x = team2Pos.x;
                joker2Sprite.y = team2Pos.y;
                joker2Sprite.rotation = team2Pos.rotation;
            }

            // Update history buttons positions
            const { historyBtn1, historyBtn2 } = this.sprites;
            if (historyBtn1 && team1CardSprite) {
                historyBtn1.x = team1CardSprite.x + width / 2;
                historyBtn1.y = team1CardSprite.y - 30;
            }
            if (historyBtn2 && team2CardSprite) {
                historyBtn2.x = team2CardSprite.x + height / 2; // Note: rotated logic might need adjustment but relying on previous logic
                historyBtn2.y = team2CardSprite.y + 30;
            }
        }
    }
}

export default ScoreMarkerRenderer;
